// The RFP email, as data.
//
// The broker previews this on screen (ConsolidatedRFPEmail.tsx) and carriers
// receive it as HTML (EmailTemplates.ts). Both render FROM HERE. Building the
// body twice guaranteed drift — the preview would quietly stop matching what
// carriers actually got, and the preview is the only thing the broker checks
// before sending.
//
// Values fall back to "Not specified" rather than being omitted: a carrier
// needs to see that a question was left open, otherwise they quote to their own
// default and the proposals stop being comparable.

export const NOT_SPECIFIED = "Not specified";

export type EmailItem = { label: string; value: string };
export type EmailSection = { coverageType: string; label: string; items: EmailItem[] };

export type RfpEmailContent = {
  /**
   * The word before the recipient's first name: "Dear" in "Dear Jill,". The
   * name itself is never in here — it differs per copy of the email, and the
   * greeting is assembled where that name is known.
   */
  salutation: string;
  subject: string;
  intro: string;
  /**
   * Who sent it: name, agency, contact details, licence. One line each, already
   * stripped of the ones with no data — a carrier should never receive a blank
   * line where a phone number was meant to be.
   */
  signature: string[];
  facts: EmailItem[];
  sections: EmailSection[];
  customMessage: string | null;
  closing: string[];
};

const VOLUNTARY_BENEFIT_LABELS: Record<string, string> = {
  accident: "Accident Insurance",
  critical_illness: "Critical Illness Insurance",
  cancer: "Cancer Insurance",
  hospital_indemnity: "Hospital Indemnity",
  gap: "Gap Insurance",
  pet: "Pet Insurance",
  legal: "Legal Services",
  identity_theft: "Identity Theft Protection",
  whole_life: "Whole Life Insurance",
  universal_life: "Universal Life Insurance",
};

const SECTION_LABELS: Record<string, string> = {
  // health is what the wizard stores, medical the normalised form of it.
  health: "Medical",
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
  life: "Life & AD&D",
  disability: "Disability",
  voluntary: "Voluntary Benefits",
};

const money = (n: unknown): string | null =>
  typeof n === "number" && n > 0 ? `$${n.toLocaleString("en-US")}` : null;

const words = (s: unknown): string | null =>
  typeof s === "string" && s.trim() ? s.replace(/_/g, " ") : null;

/**
 * "fully_insured" → "Fully insured". `words` alone left values lower case
 * mid-sentence in a table of capitalised ones.
 */
const sentence = (s: unknown): string | null => {
  const text = words(s);
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
};

/** Network names are initialisms: "hmo" reads wrong anywhere but upper case. */
const upper = (s: unknown): string | null =>
  typeof s === "string" && s.trim() ? s.trim().toUpperCase() : null;

const or = (...candidates: (string | null | undefined)[]): string =>
  candidates.find((c) => c != null && c !== "") ?? NOT_SPECIFIED;

/** Unlike `money`, keeps 0: a $0 copay is an answer, not a blank. */
const dollars = (n: unknown): string | null =>
  typeof n === "number" && Number.isFinite(n) ? `$${n.toLocaleString("en-US")}` : null;

const percent = (s: unknown): string | null =>
  typeof s === "string" && s.trim() ? `${s.trim()}%` : null;

/** "$1,000 individual / $2,000 family", or whichever half was answered. */
const perTier = (individual: unknown, family: unknown): string | null => {
  const parts = [
    dollars(individual) && `${dollars(individual)} individual`,
    dollars(family) && `${dollars(family)} family`,
  ].filter(Boolean);
  return parts.length ? parts.join(" / ") : null;
};

function healthItems(p: any): EmailItem[] {
  const items: EmailItem[] = [
    { label: "Network type", value: or(p?.networkTypes?.map(upper).join(", "), "Any") },
    {
      label: "Funding",
      value: or(p?.fundingTypes?.map(sentence).join(", ")),
    },
    { label: "Deductible", value: or(perTier(p?.deductibleIndividual, p?.deductibleFamily)) },
    { label: "Coinsurance", value: or(percent(p?.coinsurance)) },
    {
      label: "Out-of-pocket maximum",
      value: or(perTier(p?.outOfPocketIndividual, p?.outOfPocketFamily)),
    },
    {
      label: "Copays",
      value: or(
        [
          dollars(p?.copayOffice) && `${dollars(p.copayOffice)} office`,
          dollars(p?.copaySpecialist) && `${dollars(p.copaySpecialist)} specialist`,
          dollars(p?.copayEmergencyRoom) && `${dollars(p.copayEmergencyRoom)} ER`,
        ]
          .filter(Boolean)
          .join(" / "),
      ),
    },
    {
      label: "Prescription copays",
      value: or(
        [
          dollars(p?.rxGeneric) && `${dollars(p.rxGeneric)} generic`,
          dollars(p?.rxPreferredBrand) && `${dollars(p.rxPreferredBrand)} preferred`,
          dollars(p?.rxNonPreferredBrand) && `${dollars(p.rxNonPreferredBrand)} non-preferred`,
          dollars(p?.rxSpecialty) && `${dollars(p.rxSpecialty)} specialty`,
        ]
          .filter(Boolean)
          .join(" / "),
      ),
    },
  ];

  // A requirement, not a question, so printed only when asked for: "HSA: Not
  // specified" reads as the broker not knowing.
  const accounts = [p?.hsaCompatible && "HSA", p?.hraCompatible && "HRA"].filter(Boolean);
  if (accounts.length) {
    items.push({ label: "Must be compatible with", value: accounts.join(" and ") });
  }

  const contribution = [
    percent(p?.employerContributionEmployee) &&
      `${percent(p.employerContributionEmployee)} of employee`,
    percent(p?.employerContributionDependent) &&
      `${percent(p.employerContributionDependent)} of dependents`,
  ].filter(Boolean);
  if (contribution.length) {
    items.push({ label: "Employer contribution", value: contribution.join(", ") });
  }

  // Facts about the client, not asks. Naming the incumbent is sometimes a
  // negotiating choice, so silence has to stay silent.
  if (words(p?.currentCarrier)) {
    items.push({ label: "Current carrier", value: p.currentCarrier.trim() });
  }
  if (percent(p?.renewalIncrease)) {
    items.push({ label: "Renewal increase", value: percent(p.renewalIncrease)! });
  }

  return items;
}

function dentalItems(p: any): EmailItem[] {
  const items: EmailItem[] = [
    { label: "Network type", value: or(p?.networkType?.toUpperCase()) },
    // "Custom Amount" parks the figure in customMaximum and leaves the dropdown at 0.
    { label: "Maximum benefit", value: or(money(p?.maximumBenefit), money(p?.customMaximum)) },
    { label: "Perio/endo coverage", value: or(words(p?.perioEndoCoverage)) },
    { label: "Orthodontic coverage", value: or(words(p?.orthodonticCoverage)) },
  ];
  if (p?.orthodonticCoverage !== "not_covered") {
    items.push({
      label: "Orthodontic maximum",
      value: or(money(p?.orthodonticMaximum), money(p?.customOrthoMaximum)),
    });
  }
  return items;
}

function visionItems(p: any): EmailItem[] {
  return [
    { label: "Coverage types", value: or(p?.coverageTypes?.map(words).join(", ")) },
    { label: "Materials copay", value: or(p?.materialsCopay ? `$${p.materialsCopay}` : null) },
    { label: "Benefits frequency", value: or(words(p?.benefitsFrequency)) },
    { label: "Frame allowance", value: or(money(p?.allowanceAmount)) },
  ];
}

function lifeItems(p: any): EmailItem[] {
  const items: EmailItem[] = [
    { label: "Coverage types", value: or(p?.coverageTypes?.map(words).join(", ")) },
    { label: "Voluntary buy-up", value: p?.voluntaryBuyUp ? "Yes" : "No" },
  ];
  const amounts: any[] = Array.isArray(p?.coverageAmounts) ? p.coverageAmounts : [];
  const described = amounts
    .map((a) =>
      [
        money(a?.flatAmount) ? `Flat ${money(a.flatAmount)}` : null,
        a?.salaryMultiple ? `${a.salaryMultiple}x salary` : null,
        money(a?.maximum) ? `max ${money(a.maximum)}` : null,
      ]
        .filter(Boolean)
        .join(", "),
    )
    .filter((s) => s.length > 0);
  // A row exists by default but stays empty until something is entered — an
  // empty row asks the carrier for nothing, so don't list it as an option.
  described.forEach((desc, i) => items.push({ label: `Option ${i + 1}`, value: desc }));
  if (!described.length) items.push({ label: "Coverage amount", value: NOT_SPECIFIED });
  return items;
}

function disabilityItems(p: any): EmailItem[] {
  const items: EmailItem[] = [
    {
      label: "Coverage types",
      value: or(
        p?.coverageTypes
          ?.map((t: string) => (t === "short_term" || t === "std" ? "STD" : "LTD"))
          .join(", "),
      ),
    },
  ];
  if (p?.stdParams) {
    const s = p.stdParams;
    items.push(
      { label: "STD — elimination period", value: or(words(s.eliminationPeriod)) },
      { label: "STD — benefit duration", value: or(words(s.benefitDuration)) },
      { label: "STD — benefit percentage", value: s.benefitPercentage ? `${s.benefitPercentage}%` : NOT_SPECIFIED },
      {
        label: "STD — maximum weekly benefit",
        value: or(
          s.maximumWeeklyBenefit === "custom" ? money(s.customMaxWeeklyBenefit) : money(Number(s.maximumWeeklyBenefit)),
        ),
      },
      {
        label: "STD — minimum weekly benefit",
        value: s.minimumWeeklyBenefit === "none" ? "None" : or(money(Number(s.minimumWeeklyBenefit))),
      },
    );
  }
  if (p?.ltdParams) {
    const l = p.ltdParams;
    items.push(
      { label: "LTD — elimination period", value: or(words(l.eliminationPeriod)) },
      { label: "LTD — benefit duration", value: or(words(l.benefitDuration)) },
      { label: "LTD — benefit percentage", value: l.benefitPercentage ? `${l.benefitPercentage}%` : NOT_SPECIFIED },
      {
        label: "LTD — maximum monthly benefit",
        value: or(
          l.maximumMonthlyBenefit === "custom" ? money(l.customMaxMonthlyBenefit) : money(Number(l.maximumMonthlyBenefit)),
        ),
      },
      { label: "LTD — definition of disability", value: or(words(l.definitionOfDisability)) },
      { label: "LTD — covered earnings", value: or(words(l.coveredEarningsDefinition)) },
    );
  }
  return items;
}

function voluntaryItems(p: any): EmailItem[] {
  const selected: string[] = Array.isArray(p?.selectedBenefits) ? p.selectedBenefits : [];
  return [
    {
      label: "Benefits to quote",
      value: selected.length
        ? selected.map((b) => VOLUNTARY_BENEFIT_LABELS[b] ?? b).join(", ")
        : NOT_SPECIFIED,
    },
  ];
}

const BUILDERS: Record<string, (p: any) => EmailItem[]> = {
  // Both keys: the wizard stores "health", but plan data elsewhere says
  // "medical", and either can reach here from a saved draft.
  health: healthItems,
  medical: healthItems,
  dental: dentalItems,
  vision: visionItems,
  life: lifeItems,
  disability: disabilityItems,
  voluntary: voluntaryItems,
};

/**
 * The label/value rows an RFP email prints for one coverage line, or null when
 * that line has no form of its own.
 *
 * Exported so a screen showing what was asked renders the same rows the carrier
 * received, rather than a second formatter that drifts from this one.
 */
export function coverageItems(coverageType: string, params: unknown): EmailItem[] | null {
  const build = BUILDERS[coverageType?.toLowerCase()];
  return build ? build(params ?? {}) : null;
}

export type BuildRfpEmailInput = {
  coverageTypes: string[];
  rfpParameters: Record<string, any>;
  companyName: string;
  effectiveDate: Date | string;
  employeeCount: number;
  customMessage?: string | null;
  /** Defaults to two weeks out — the deadline the closing paragraph quotes. */
  respondBy?: Date | string;
  /**
   * The broker's own wording, replacing what this builds. Blank or absent keeps
   * the generated text, so a draft saved before this existed still reads right.
   *
   * Prose only: facts, the carrier list and the coverage sections are never
   * overridable, because they are per recipient. A GA is asked for the carriers
   * it can place and a carrier sees only its own lines, and a single block of
   * typed-over text cannot express either.
   */
  greeting?: string | null;
  intro?: string | null;
  closing?: string | null;
  signature?: string | null;
  /**
   * The broker and their agency, used to build the default signature. The
   * agency's licence, not the individual agent's: a carrier is being asked to
   * quote through the agency.
   */
  sender?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    agencyName?: string | null;
    agencyLicenseNumber?: string | null;
  } | null;
  /**
   * Set only when the RFP goes to a general agent rather than to each carrier.
   * Naming the carriers IS the request for a GA; without it the email says which
   * coverages but never which markets to approach. A carrier already knows it is
   * being asked, so it never sees this.
   */
  carrierNames?: string[];
  /**
   * The general agency this copy is going to. Named in the subject so a GA
   * working several RFPs can tell at a glance which one is theirs, and how much
   * is being asked of them.
   *
   * GA mode only. A carrier's subject must not name anybody: it would leak that
   * the RFP is being worked through a wholesaler, and to which one.
   */
  recipientLabel?: string;
};

// A date-only string is UTC midnight to `new Date`, so rendering it in a
// timezone behind UTC shows the day before — carriers would be told the wrong
// effective date. Read the calendar parts directly instead. Mirrors
// parseDateLocal in client/src/lib/utils.ts, which can't be imported here.
const formatDate = (d: Date | string): string => {
  const dateOnly = typeof d === "string" ? d.slice(0, 10).match(/^\d{4}-\d{2}-\d{2}$/)?.[0] : null;
  const local = dateOnly
    ? new Date(Number(dateOnly.slice(0, 4)), Number(dateOnly.slice(5, 7)) - 1, Number(dateOnly.slice(8, 10)))
    : new Date(d);
  return local.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/** Two weeks out, which is what the trade expects when nobody says otherwise. */
export const defaultRespondBy = (from: Date = new Date()): Date =>
  new Date(from.getTime() + 14 * 24 * 60 * 60 * 1000);

/** Blank-line-separated paragraphs, blanks dropped. Empty means "not set". */
const paragraphsOf = (text: string | null | undefined): string[] =>
  (text ?? "")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

/** The default sign-off, empty lines dropped. One line per detail. */
export function signatureLines(sender: BuildRfpEmailInput["sender"]): string[] {
  const contact = [sender?.email, sender?.phone].map(words).filter(Boolean);
  return [
    words(sender?.name),
    words(sender?.agencyName),
    contact.length ? contact.join(" · ") : null,
    sender?.agencyLicenseNumber?.trim()
      ? `License ${sender.agencyLicenseNumber.trim()}`
      : null,
  ].filter((line): line is string => !!line);
}

export function buildRfpEmailContent(input: BuildRfpEmailInput): RfpEmailContent {
  const respondBy = input.respondBy ?? defaultRespondBy();

  const sections: EmailSection[] = [];
  for (const type of input.coverageTypes) {
    const build = BUILDERS[type];
    const params = input.rfpParameters?.[type];
    // Every picked coverage gets a section, form or no form. Medical has no
    // form, and skipping it here sent RFPs that never mentioned a line the
    // broker had asked for. No form now reads the same as an empty one.
    sections.push({
      coverageType: type,
      label: SECTION_LABELS[type] ?? type,
      items: build
        ? build(params ?? {})
        : [{ label: "Requirements", value: NOT_SPECIFIED }],
    });
  }

  return {
    // One source for both sides, or the preview greets differently from the
    // email, which is exactly what it used to do.
    salutation: input.greeting?.trim() || "Dear",
    subject:
      input.recipientLabel && input.carrierNames?.length
        ? `Request for Proposal — ${input.companyName} · ${input.carrierNames.length} ${
            input.carrierNames.length === 1 ? "carrier" : "carriers"
          } via ${input.recipientLabel}`
        : `Request for Proposal — ${input.companyName}`,
    intro:
      input.intro?.trim() ||
      (input.carrierNames?.length
        ? `We are requesting proposals for the following coverage types on behalf of our client, ${input.companyName}. Please obtain quotes from the carriers listed below.`
        : `We are requesting proposals for the following coverage types on behalf of our client, ${input.companyName}.`),
    facts: [
      { label: "Company", value: input.companyName },
      { label: "Effective date", value: formatDate(input.effectiveDate) },
      { label: "Eligible employees", value: String(input.employeeCount) },
      // A labelled row, not a sentence buried in the closing. It survives the
      // broker rewriting the prose, and a carrier scanning the email finds the
      // date where it finds the other dates.
      { label: "Quotes due", value: formatDate(respondBy) },
      ...(input.carrierNames?.length
        ? [{ label: "Carriers to quote", value: input.carrierNames.join(", ") }]
        : []),
    ],
    sections,
    customMessage: input.customMessage?.trim() ? input.customMessage.trim() : null,
    // Prose only. The deadline is a fact row above, so rewriting this cannot
    // lose it, and the broker is not made to retype a date to change a sentence.
    closing: paragraphsOf(input.closing).length
      ? paragraphsOf(input.closing)
      : [
          "Please provide your most competitive rates and plan options that meet these requirements.",
          "If you have any questions or need additional information, please don't hesitate to contact us.",
          "Thank you for your consideration.",
        ],
    // A rewritten signature replaces the lot. One line per line typed, blanks
    // dropped, so an extra return does not become an empty line in the email.
    signature: input.signature?.trim()
      ? input.signature
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      : signatureLines(input.sender),
  };
}

/** Narrow the content to one recipient's coverages — carriers only quote their own lines. */
export function contentForRecipient(
  content: RfpEmailContent,
  coverageTypes: string[] | undefined,
): RfpEmailContent {
  if (!coverageTypes?.length) return content;
  return { ...content, sections: content.sections.filter((s) => coverageTypes.includes(s.coverageType)) };
}

/**
 * The message a given recipient sees. A per-recipient note replaces the shared
 * one rather than stacking with it — two blocks of "additional notes" in one
 * email reads as a mistake, and the broker writing a carrier-specific note is
 * usually saying something instead of, not as well as, the general line.
 */
export function messageFor(
  recipientId: string,
  sharedMessage: string | null | undefined,
  perRecipient: Record<string, string> | undefined,
): string | null {
  const specific = perRecipient?.[recipientId]?.trim();
  if (specific) return specific;
  const shared = sharedMessage?.trim();
  return shared ? shared : null;
}
